/**
 * M2M Relation Interface
 * Allows managing many-to-many relationships through a junction table
 *
 * Key concept: M2M data is fetched from the JUNCTION TABLE, not the related table
 * The junction table is filtered by the current item's primary key
 */

import { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
	Plus,
	Check,
	Search,
	ChevronLeft,
	ChevronRight,
	Trash2,
	ExternalLink,
	GripVertical,
	Link2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useItems } from '@/hooks/useItems';
import { useFields } from '@/hooks/useFields';
import { useCollection } from '@/hooks/useCollections';

// Types for M2M junction items
export interface JunctionItem {
	id?: number | string;
	[key: string]: unknown;
	// These are the standard staged item markers
	$type?: 'created' | 'updated' | 'deleted';
	$index?: number;
}

// The value format expected by the backend
export interface M2MValue {
	create: Record<string, unknown>[];
	update: Record<string, unknown>[];
	delete: (string | number)[];
}

interface M2MRelationInterfaceProps {
	value: M2MValue | JunctionItem[] | null | undefined;
	onChange: (value: M2MValue) => void;
	// Relation info
	junctionCollection: string;
	relatedCollection: string;
	junctionFieldCurrent: string; // Field in junction pointing to current item (e.g., post_id)
	junctionFieldRelated: string; // Field in junction pointing to related item (e.g., Nutrition_Recommendations_id)
	currentPrimaryKey: string | number | null;
	// Display options
	displayTemplate?: string;
	disabled?: boolean;
	enableCreate?: boolean;
	enableSelect?: boolean;
	enableLink?: boolean;
	/** Items per page in the selection drawer (default 25) */
	limit?: number;
}

// Helper to parse the value into the normalized M2MValue structure
function normalizeValue(value: M2MValue | JunctionItem[] | null | undefined): M2MValue {
	if (!value) {
		return { create: [], update: [], delete: [] };
	}

	if (Array.isArray(value)) {
		// Legacy array format - convert to M2MValue
		return { create: [], update: [], delete: [] };
	}

	return {
		create: value.create || [],
		update: value.update || [],
		delete: value.delete || [],
	};
}

export function M2MRelationInterface({
	value,
	onChange,
	junctionCollection,
	relatedCollection,
	junctionFieldCurrent,
	junctionFieldRelated,
	currentPrimaryKey,
	displayTemplate,
	disabled = false,
	enableCreate = true,
	enableSelect = true,
	enableLink = false,
	limit = 25,
}: M2MRelationInterfaceProps) {
	const [selectSheetOpen, setSelectSheetOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	// Track items selected in the sheet (for multi-select before confirming)
	const [pendingSelections, setPendingSelections] = useState<Set<string | number>>(new Set());

	// Fetch related collection info
	const { data: collectionInfo } = useCollection(relatedCollection);
	const { data: _junctionCollectionInfo } = useCollection(junctionCollection);

	// Fetch fields for the junction collection
	const { fields: junctionFields, loading: junctionFieldsLoading } = useFields({ collection: junctionCollection });

	// Fetch fields for the related collection
	const { fields: relatedFields, loading: relatedFieldsLoading } = useFields({ collection: relatedCollection });

	// Get primary key fields
	const junctionPrimaryKeyField = useMemo(() => {
		return junctionFields.find((f) => f.schema?.is_primary_key)?.field || 'id';
	}, [junctionFields]);

	const relatedPrimaryKeyField = useMemo(() => {
		return relatedFields.find((f) => f.schema?.is_primary_key)?.field || 'id';
	}, [relatedFields]);

	// Get display fields for the related collection
	const relatedDisplayFields = useMemo(() => {
		return relatedFields
			.filter((f) => !f.meta?.hidden && f.field !== relatedPrimaryKeyField)
			.slice(0, 3)
			.map((f) => f.field);
	}, [relatedFields, relatedPrimaryKeyField]);

	// Build query fields for junction table
	// Note: Only request fields that actually exist on the junction table
	// The backend will expand the junctionFieldRelated to include the foreign key value
	const junctionQueryFields = useMemo(() => {
		const fields = new Set<string>([
			junctionPrimaryKeyField,
			junctionFieldCurrent,
			junctionFieldRelated, // This will get the related item's ID
		]);

		return Array.from(fields);
	}, [junctionPrimaryKeyField, junctionFieldCurrent, junctionFieldRelated]);

	// Normalize the value
	const normalizedValue = useMemo(() => normalizeValue(value), [value]);

	// Build query to fetch existing junction items for the current item
	const junctionQuery = useMemo(() => {
		if (!currentPrimaryKey || currentPrimaryKey === '+') {
			return null;
		}

		return {
			fields: junctionQueryFields,
			filter: {
				_and: [{ [junctionFieldCurrent]: currentPrimaryKey }],
			},
			limit,
			page: 1,
		};
	}, [junctionQueryFields, junctionFieldCurrent, currentPrimaryKey, limit]);

	// Fetch existing junction items from the junction table
	const {
		data: junctionData,
		isLoading: junctionLoading,
		refetch: _refetchJunction,
	} = useItems(junctionCollection, junctionQuery || { fields: [junctionPrimaryKeyField], limit: 0 }, {
		enabled: !!junctionQuery,
	});

	// Get the fetched junction items
	const fetchedJunctionItems = useMemo(() => {
		return junctionData?.items || [];
	}, [junctionData]);

	// Get IDs of items marked for deletion
	const deletedIds = useMemo(() => new Set(normalizedValue.delete), [normalizedValue.delete]);

	// Helper to extract related ID from junction item
	const getRelatedId = useCallback(
		(junctionItem: Record<string, unknown>): string | number | undefined => {
			const relatedValue = junctionItem[junctionFieldRelated];
			// Could be just an ID or an object with the ID
			if (typeof relatedValue === 'object' && relatedValue !== null) {
				return (relatedValue as Record<string, unknown>)[relatedPrimaryKeyField] as string | number | undefined;
			}
			return relatedValue as string | number | undefined;
		},
		[junctionFieldRelated, relatedPrimaryKeyField]
	);

	// Extract all related IDs from junction items + created items (for fetching display data)
	const allRelatedIds = useMemo(() => {
		const ids: (string | number)[] = [];

		for (const item of fetchedJunctionItems) {
			const junctionId = item[junctionPrimaryKeyField] as string | number;
			if (!deletedIds.has(junctionId)) {
				const relatedId = getRelatedId(item);
				if (relatedId != null) ids.push(relatedId);
			}
		}

		for (const createItem of normalizedValue.create) {
			const relatedId = getRelatedId(createItem);
			if (relatedId != null) ids.push(relatedId);
		}

		return [...new Set(ids)]; // dedupe
	}, [fetchedJunctionItems, normalizedValue.create, junctionPrimaryKeyField, deletedIds, getRelatedId]);

	// Fetch related items for display
	const relatedItemsQuery = useMemo(() => {
		if (allRelatedIds.length === 0) return null;
		return {
			fields: [relatedPrimaryKeyField, ...relatedDisplayFields],
			filter: { [relatedPrimaryKeyField]: { _in: allRelatedIds } },
			limit: allRelatedIds.length,
		};
	}, [allRelatedIds, relatedPrimaryKeyField, relatedDisplayFields]);

	const { data: relatedItemsData } = useItems(
		relatedCollection,
		relatedItemsQuery || { fields: [relatedPrimaryKeyField], limit: 0 },
		{ enabled: !!relatedItemsQuery }
	);

	// Map of related items by ID for quick lookup
	const relatedItemsMap = useMemo(() => {
		const map = new Map<string | number, Record<string, unknown>>();
		for (const item of relatedItemsData?.items || []) {
			const id = item[relatedPrimaryKeyField] as string | number;
			if (id != null) map.set(id, item);
		}
		return map;
	}, [relatedItemsData?.items, relatedPrimaryKeyField]);

	// Format display value using template (defined before displayItems to avoid hoisting issues)
	const formatDisplayValue = useCallback(
		(junctionItem: Record<string, unknown>, relatedItem?: Record<string, unknown>): string => {
			if (!junctionItem) return '';

			if (displayTemplate) {
				return displayTemplate.replace(/\{\{([\w.]+)\}\}/g, (_, fieldPath) => {
					// Handle nested field paths like Nutrition_Recommendations_id.name
					const parts = fieldPath.split('.');
					let value: unknown;

					// First check if it's a junction table field
					if (parts.length === 1) {
						value = junctionItem[parts[0]];
					} else if (parts[0] === junctionFieldRelated && relatedItem) {
						// It's a related field (e.g., Nutrition_Recommendations_id.name)
						value = relatedItem;
						for (let i = 1; i < parts.length; i++) {
							if (value && typeof value === 'object') {
								value = (value as Record<string, unknown>)[parts[i]];
							} else {
								value = undefined;
								break;
							}
						}
					} else {
						// Try full path on junction item
						value = junctionItem;
						for (const part of parts) {
							if (value && typeof value === 'object') {
								value = (value as Record<string, unknown>)[part];
							} else {
								value = undefined;
								break;
							}
						}
					}
					return String(value ?? '');
				});
			}

			// Default: try to get related item's first display field
			if (relatedItem) {
				const firstField = relatedDisplayFields[0];
				if (firstField && relatedItem[firstField]) {
					return String(relatedItem[firstField]);
				}
				return String(relatedItem[relatedPrimaryKeyField] || '');
			}

			// Fallback: show the related ID from junction item
			const relatedId = getRelatedId(junctionItem);
			return String(relatedId || junctionItem[junctionPrimaryKeyField] || '(new item)');
		},
		[
			displayTemplate,
			junctionFieldRelated,
			relatedDisplayFields,
			relatedPrimaryKeyField,
			junctionPrimaryKeyField,
			getRelatedId,
		]
	);

	// Build display items combining fetched + created - deleted
	const displayItems = useMemo(() => {
		const items: Array<{
			junctionId: string | number | undefined;
			relatedId: string | number | undefined;
			displayValue: string;
			junctionItem: Record<string, unknown>;
			$type?: 'created' | 'updated' | 'deleted';
			$index?: number;
		}> = [];

		// Add fetched items (mark deleted ones)
		for (const junctionItem of fetchedJunctionItems) {
			const junctionId = junctionItem[junctionPrimaryKeyField] as string | number;
			const isDeleted = deletedIds.has(junctionId);

			// Check if this item has updates
			const updateItem = normalizedValue.update.find((u) => u[junctionPrimaryKeyField] === junctionId);

			const mergedItem = updateItem ? { ...junctionItem, ...updateItem } : junctionItem;
			const relatedId = getRelatedId(mergedItem);
			const relatedItem = relatedId != null ? relatedItemsMap.get(relatedId) : undefined;

			items.push({
				junctionId,
				relatedId,
				displayValue: formatDisplayValue(mergedItem, relatedItem),
				junctionItem: mergedItem,
				$type: isDeleted ? 'deleted' : updateItem ? 'updated' : undefined,
				$index: isDeleted ? normalizedValue.delete.indexOf(junctionId) : undefined,
			});
		}

		// Add newly created items
		normalizedValue.create.forEach((createItem, index) => {
			const relatedId = getRelatedId(createItem);
			const relatedItem = relatedId != null ? relatedItemsMap.get(relatedId) : undefined;

			items.push({
				junctionId: undefined,
				relatedId,
				displayValue: formatDisplayValue(createItem, relatedItem),
				junctionItem: createItem,
				$type: 'created',
				$index: index,
			});
		});

		// Filter out deleted items for display (but keep them in the list for visual feedback if needed)
		return items.filter((item) => item.$type !== 'deleted');
	}, [
		fetchedJunctionItems,
		normalizedValue,
		junctionPrimaryKeyField,
		deletedIds,
		getRelatedId,
		relatedItemsMap,
		formatDisplayValue,
	]);

	// Get already selected related item IDs (for visual marking in the selection drawer).
	// Uses getRelatedId() which correctly handles both raw-ID values and object-shaped values
	// from the junction table.
	const selectedRelatedIds = useMemo(() => {
		const ids: (string | number)[] = [];

		// From fetched junction items (excluding deleted)
		for (const item of fetchedJunctionItems) {
			const junctionId = item[junctionPrimaryKeyField] as string | number;
			if (!deletedIds.has(junctionId)) {
				const relatedId = getRelatedId(item);
				if (relatedId != null) ids.push(relatedId);
			}
		}

		// From newly-created (pending) items
		for (const createItem of normalizedValue.create) {
			const relatedId = getRelatedId(createItem);
			if (relatedId != null) ids.push(relatedId);
		}

		return ids;
	}, [
		fetchedJunctionItems,
		normalizedValue.create,
		junctionPrimaryKeyField,
		deletedIds,
		getRelatedId,
	]);

	// Build query for selecting new items (from related collection) — shows ALL items,
	// including already-linked ones (they are visually marked in the UI instead of excluded).
	const selectQueryFields = useMemo(() => {
		return [relatedPrimaryKeyField, ...relatedDisplayFields];
	}, [relatedPrimaryKeyField, relatedDisplayFields]);

	const selectQuery = useMemo(
		() => ({
			fields: selectQueryFields,
			limit,
			page,
			search: search || undefined,
			// No _nin filter: show every item so the user can see what's already linked
			meta: ['total_count', 'filter_count'],
		}),
		[selectQueryFields, limit, page, search]
	);

	// Fetch available items for selection
	const { data: itemsData, isLoading: itemsLoading } = useItems(relatedCollection, selectQuery, {
		enabled: selectSheetOpen,
	});

	const availableItems = itemsData?.items || [];
	// filter_count reflects the count after search/filter; fall back to total_count
	const totalCount = itemsData?.meta?.filter_count ?? itemsData?.meta?.total_count ?? 0;
	const totalPages = totalCount > 0 ? Math.ceil(totalCount / limit) : 1;

	// Set of related IDs already linked to this record (used for visual marking in the drawer)
	const selectedRelatedIdsSet = useMemo(() => new Set(selectedRelatedIds.map(String)), [selectedRelatedIds]);

	// Toggle item selection in pending selections (for multi-select)
	// Already-linked items cannot be re-added.
	const togglePendingSelection = (itemId: string | number) => {
		if (selectedRelatedIdsSet.has(String(itemId))) return; // already linked
		setPendingSelections((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(itemId)) {
				newSet.delete(itemId);
			} else {
				newSet.add(itemId);
			}
			return newSet;
		});
	};

	// Confirm all pending selections - adds to create array
	const confirmSelections = () => {
		if (pendingSelections.size === 0) {
			setSelectSheetOpen(false);
			return;
		}

		const newCreateItems: Record<string, unknown>[] = [];

		pendingSelections.forEach((relatedId) => {
			newCreateItems.push({
				[junctionFieldCurrent]: currentPrimaryKey,
				[junctionFieldRelated]: {
					[relatedPrimaryKeyField]: relatedId,
				},
			});
		});

		onChange({
			...normalizedValue,
			create: [...normalizedValue.create, ...newCreateItems],
		});

		setPendingSelections(new Set());
		setSelectSheetOpen(false);
		setSearch('');
		setPage(1);
	};

	// Remove item - marks for deletion or removes from create array
	const handleRemove = (item: (typeof displayItems)[0]) => {
		if (item.$type === 'created' && item.$index !== undefined) {
			// Remove from create array
			const newCreate = [...normalizedValue.create];
			newCreate.splice(item.$index, 1);
			onChange({
				...normalizedValue,
				create: newCreate,
			});
		} else if (item.junctionId != null) {
			// Mark existing item for deletion
			onChange({
				...normalizedValue,
				delete: [...normalizedValue.delete, item.junctionId],
			});
		}
	};

	const fieldsLoading = junctionFieldsLoading || relatedFieldsLoading;

	return (
		<div className="space-y-3">
			{/* Selected Items List */}
			<div className="rounded-lg border">
				{junctionLoading || fieldsLoading ? (
					<div className="p-4 space-y-2">
						{Array.from({ length: 3 }).map((_, i) => (
							<Skeleton key={i} className="h-10 w-full" />
						))}
					</div>
				) : displayItems.length === 0 ? (
					<div className="flex h-20 items-center justify-center text-sm text-muted-foreground">No items selected</div>
				) : (
					<ScrollArea className={cn('max-h-[300px]', displayItems.length > 5 && 'h-[300px]')}>
						<div className="divide-y">
							{displayItems.map((item, idx) => (
								<div
									key={item.junctionId ?? `new-${idx}`}
									className={cn(
										'flex items-center gap-3 px-3 py-2 hover:bg-accent/50',
										item.$type === 'created' && 'bg-green-50/50 dark:bg-green-950/20'
									)}
								>
									<GripVertical className="h-4 w-4 text-muted-foreground/50 cursor-grab" />

									<div className="flex-1 min-w-0">
										<span className="text-sm truncate">{item.displayValue}</span>
										{item.$type === 'created' && (
											<Badge variant="outline" className="ml-2 text-[10px]">
												New
											</Badge>
										)}
									</div>

									<div className="flex items-center gap-1">
										{enableLink && item.relatedId && (
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="h-7 w-7"
												onClick={() => {
													window.open(`/admin/content/${relatedCollection}/${item.relatedId}`, '_blank');
												}}
											>
												<ExternalLink className="h-3.5 w-3.5" />
											</Button>
										)}

										{!disabled && (
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
												onClick={() => handleRemove(item)}
											>
												<Trash2 className="h-3.5 w-3.5" />
											</Button>
										)}
									</div>
								</div>
							))}
						</div>
					</ScrollArea>
				)}
			</div>

			{/* Actions */}
			{!disabled && (enableSelect || enableCreate) && (
				<div className="flex gap-2">
					{enableSelect && (
						<Button type="button" variant="outline" size="sm" onClick={() => setSelectSheetOpen(true)}>
							<Plus className="h-4 w-4 mr-1" />
							Add Existing
						</Button>
					)}
					{enableCreate && (
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled // TODO: Implement create new
						>
							<Plus className="h-4 w-4 mr-1" />
							Create New
						</Button>
					)}
				</div>
			)}

			{/* Selection Sheet */}
			<Sheet open={selectSheetOpen} onOpenChange={setSelectSheetOpen}>
				<SheetContent side="right" className="w-full sm:max-w-xl">
					<SheetHeader>
						<SheetTitle>Add {collectionInfo?.meta?.collection || relatedCollection}</SheetTitle>
						<SheetDescription>
							Select items to link. Items marked with <Link2 className="inline h-3.5 w-3.5 text-blue-500" /> are already
							linked.
						</SheetDescription>
					</SheetHeader>

					<div className="mt-4 space-y-4">
						{/* Summary badges */}
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<span className="flex items-center gap-1">
								<span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-100 border border-blue-300" />
								Already linked: <strong className="text-foreground">{selectedRelatedIds.length}</strong>
							</span>
							{pendingSelections.size > 0 && (
								<span className="flex items-center gap-1 text-primary">
									· Pending: <strong>{pendingSelections.size}</strong>
								</span>
							)}
						</div>

						{/* Search */}
						<div className="relative">
							<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								placeholder="Search..."
								value={search}
								onChange={(e) => {
									setSearch(e.target.value);
									setPage(1);
								}}
								className="pl-10"
							/>
						</div>

						{/* Items Table — Tooltip wraps TooltipProvider internally */}
						<ScrollArea className="h-[calc(100vh-340px)]">
							{itemsLoading ? (
								<div className="space-y-2">
									{Array.from({ length: 5 }).map((_, i) => (
										<Skeleton key={i} className="h-12 w-full" />
									))}
								</div>
							) : availableItems.length === 0 ? (
								<div className="flex h-32 items-center justify-center text-muted-foreground">No items available</div>
							) : (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-10">
												<span className="sr-only">Select</span>
											</TableHead>
											<TableHead className="w-16">{relatedPrimaryKeyField}</TableHead>
											{relatedDisplayFields.map((field) => (
												<TableHead key={field}>{field}</TableHead>
											))}
											<TableHead className="w-24 text-right">Status</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{availableItems.map((item: Record<string, unknown>) => {
											const itemId = item[relatedPrimaryKeyField] as string | number;
											const isAlreadyLinked = selectedRelatedIdsSet.has(String(itemId));
											const isPendingSelect = pendingSelections.has(itemId);

											const row = (
												<TableRow
													key={String(itemId)}
													className={cn(
														'transition-colors',
														isAlreadyLinked
															? 'bg-blue-50/60 dark:bg-blue-950/30 cursor-not-allowed'
															: 'cursor-pointer hover:bg-accent/50',
														isPendingSelect && !isAlreadyLinked && 'bg-primary/10'
													)}
													onClick={() => !isAlreadyLinked && togglePendingSelection(itemId)}
												>
													<TableCell>
														{isAlreadyLinked ? (
															<div className="flex h-4 w-4 items-center justify-center rounded border border-blue-400 bg-blue-100 dark:bg-blue-900">
																<Link2 className="h-2.5 w-2.5 text-blue-600" />
															</div>
														) : (
															<Checkbox
																checked={isPendingSelect}
																onCheckedChange={() => togglePendingSelection(itemId)}
																onClick={(e) => e.stopPropagation()}
															/>
														)}
													</TableCell>
													<TableCell className={cn('font-medium', isAlreadyLinked && 'text-muted-foreground')}>
														{String(itemId)}
													</TableCell>
													{relatedDisplayFields.map((field) => (
														<TableCell key={field} className={cn(isAlreadyLinked && 'text-muted-foreground')}>
															{item[field] != null ? String(item[field]) : '-'}
														</TableCell>
													))}
													<TableCell className="text-right">
														{isAlreadyLinked && (
															<Badge
																variant="outline"
																className="text-[10px] border-blue-300 text-blue-600 bg-blue-50 dark:bg-blue-950"
															>
																Linked
															</Badge>
														)}
													</TableCell>
												</TableRow>
											);

											// Wrap already-linked rows in a tooltip for extra clarity
											if (isAlreadyLinked) {
												return (
													<Tooltip key={String(itemId)}>
														<TooltipTrigger asChild>{row}</TooltipTrigger>
														<TooltipContent side="left">Already linked to this record</TooltipContent>
													</Tooltip>
												);
											}

											return row;
										})}
									</TableBody>
								</Table>
							)}
						</ScrollArea>

						{/* Pagination — always visible when there are items */}
						<div className="flex items-center justify-between border-t pt-4">
							<span className="text-sm text-muted-foreground">
								{totalCount > 0
									? `${(page - 1) * limit + 1}–${Math.min(page * limit, totalCount)} of ${totalCount}`
									: 'No items'}
							</span>
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setPage((p) => Math.max(1, p - 1))}
									disabled={page === 1 || itemsLoading}
								>
									<ChevronLeft className="h-4 w-4" />
								</Button>
								<span className="text-xs text-muted-foreground px-1">
									{page} / {totalPages}
								</span>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
									disabled={page >= totalPages || itemsLoading}
								>
									<ChevronRight className="h-4 w-4" />
								</Button>
							</div>
						</div>

						{/* Actions */}
						<div className="flex items-center gap-2 border-t pt-4">
							{pendingSelections.size > 0 && (
								<span className="text-sm text-muted-foreground">
									{pendingSelections.size} item{pendingSelections.size !== 1 ? 's' : ''} selected
								</span>
							)}
							<div className="flex-1" />
							<Button
								variant="outline"
								onClick={() => {
									setPendingSelections(new Set());
									setSelectSheetOpen(false);
									setSearch('');
									setPage(1);
								}}
							>
								Cancel
							</Button>
							<Button onClick={confirmSelections} disabled={pendingSelections.size === 0}>
								<Check className="h-4 w-4 mr-2" />
								Add Selected ({pendingSelections.size})
							</Button>
						</div>
					</div>
				</SheetContent>
			</Sheet>
		</div>
	);
}
